/**
 * CompareChart — bar-chart card that compares the rows the user has
 * selected in a DataGrid.
 *
 * The page tells us:
 *   - which rows are selected (`rows` prop, already filtered to selection)
 *   - what metrics are comparable (`metrics` prop -- array of
 *     `{ field, label, format? }` describing each numeric column)
 *   - how to label each row (`labelField`, default "name")
 *
 * The user picks one metric from a dropdown; we render a single-series
 * bar chart and let the chart decide its own Y-axis scale (so a rating
 * 0-5 metric and a downloads metric both look sensible).
 *
 * The selection cap is enforced by the parent page (we just render
 * whatever rows we get) -- typically 10 rows max to keep bars readable.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import SectionCard from './SectionCard';
import EmptyState from './EmptyState';

const defaultFormat = (v) =>
  v == null ? '—' : Number(v).toLocaleString('tr-TR');

export default function CompareChart({
  title = 'Comparison',
  description,
  rows = [],
  metrics = [],
  labelField = 'name',
  defaultMetric,
  height = 360,
  emptyMessage = 'Pick a few rows from the table above to compare.',
  /** caller-provided right-rail content (e.g. the "X / 10 selected" chip) */
  rightSlot,
}) {
  const theme = useTheme();
  const [metricKey, setMetricKey] = useState(
    defaultMetric || metrics[0]?.field || '',
  );

  // Keep the dropdown valid if the page ever swaps the metrics list.
  useEffect(() => {
    if (!metrics.length) return;
    if (!metrics.find((m) => m.field === metricKey)) {
      setMetricKey(defaultMetric || metrics[0].field);
    }
  }, [metrics, metricKey, defaultMetric]);

  const metric = useMemo(
    () => metrics.find((m) => m.field === metricKey) || null,
    [metrics, metricKey],
  );

  const chartData = useMemo(() => {
    if (!metric) return [];
    return rows.map((r, i) => ({
      // Disambiguate duplicate labels (same app on iOS + Android, etc.)
      __label: r[labelField] || `Row ${i + 1}`,
      __value: r[metric.field] == null ? null : Number(r[metric.field]),
    }));
  }, [rows, metric, labelField]);

  const formatter = metric?.format || defaultFormat;

  const titleSlot = (
    <Stack direction="row" alignItems="center" spacing={2}>
      {rightSlot}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Metric</InputLabel>
        <Select
          label="Metric"
          value={metricKey}
          onChange={(e) => setMetricKey(e.target.value)}
        >
          {metrics.map((m) => (
            <MenuItem key={m.field} value={m.field}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <SectionCard title={title} description={description} rightSlot={titleSlot}>
      {chartData.length === 0 ? (
        <EmptyState title={emptyMessage} dense />
      ) : (
        <Box sx={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 8, bottom: 24 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="__label"
                stroke={theme.palette.text.secondary}
                fontSize={12}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={70}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                fontSize={12}
                tickFormatter={(v) =>
                  metric?.shortFormat ? metric.shortFormat(v) : formatter(v)
                }
              />
              <RechartsTooltip
                formatter={(v) => [formatter(v), metric?.label || '']}
                contentStyle={{
                  borderRadius: 12,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              />
              <Legend
                payload={[
                  { value: metric?.label || '', type: 'square', color: theme.palette.primary.main },
                ]}
              />
              <Bar
                dataKey="__value"
                name={metric?.label || ''}
                fill={theme.palette.primary.main}
                radius={[6, 6, 0, 0]}
                maxBarSize={56}
              />
            </BarChart>
          </ResponsiveContainer>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, textAlign: 'center' }}
          >
            Comparing {chartData.length} item(s) on {metric?.label || ''}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
}
